import { useState, useCallback, useEffect, useMemo } from 'react';

import {
  ApiFunction,
  ApiResponse,
  SortConfig,
  PaginationConfig,
  UseDataTableReturn,
  TableConfig,
} from './types/types';

interface UseDataTableOptions<T> {
  data?: T[];
  apiCall?: ApiFunction<T>;
  config: TableConfig<T>;
}

export function useDataTable<T>({
  data = [],
  apiCall,
  config,
}: UseDataTableOptions<T>): UseDataTableReturn<T> {
  // API state
  const [apiResponse, setApiResponse] = useState<ApiResponse<T> | null>(null);
  const [apiLoading, setApiLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Pagination state
  const [pagination, setPagination] = useState<PaginationConfig>({
    page: config.initialPagination?.page || 0,
    rowsPerPage: config.initialPagination?.rowsPerPage || 50,
    totalCount: data?.length ?? 0,
    totalPages: Math.ceil((data?.length ?? 0) / (config.initialPagination?.rowsPerPage || 50)),
    rowsPerPageOptions: config.initialPagination?.rowsPerPageOptions || [20, 50, 100],
  });

  // Sort state
  const [sort, setSort] = useState<SortConfig>(
    config.initialSort || { field: null, order: 'desc' }
  );

  // Fetch data from API
  const fetchApiData = useCallback(
    async (page: number, size: number, sortConfig?: SortConfig) => {
      if (!apiCall) return;

      try {
        setApiLoading(true);
        setApiError(null);

        const response = await apiCall(page + 1, size, sortConfig); // Convert to 1-based for API

        setApiResponse(response);
      } catch (err) {
        setApiError('データの取得に失敗しました');
        console.error('API Error:', err);
      } finally {
        setApiLoading(false);
      }
    },
    [apiCall]
  );

  // Auto-fetch when using server-side features
  useEffect(() => {
    if (config.enableServerPagination && apiCall) {
      void fetchApiData(pagination.page, pagination.rowsPerPage, sort.field ? sort : undefined);
    }
  }, [
    fetchApiData,
    pagination.page,
    pagination.rowsPerPage,
    sort,
    config.enableServerPagination,
    apiCall,
  ]);

  // Pagination handlers
  const handlePageChange = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPagination((prev) => ({ ...prev, page: 0, rowsPerPage: newSize }));
  }, []);

  // Sort handler
  const handleSort = useCallback((field: string) => {
    setSort((prevSort) => {
      if (prevSort.field === field) {
        return {
          field,
          order: prevSort.order === 'asc' ? 'desc' : 'asc',
        };
      }
      return {
        field,
        order: 'desc',
      };
    });
  }, []);

  // Refetch function
  const refetch = useCallback(() => {
    if (apiCall) {
      void fetchApiData(pagination.page, pagination.rowsPerPage, sort.field ? sort : undefined);
    }
  }, [fetchApiData, pagination.page, pagination.rowsPerPage, sort]);

  // Process data based on configuration
  // Priority: data prop > apiResponse (when data prop is provided, always use it)
  const tableData = useMemo(() => {
    // If data prop is provided (even if empty array), always use it
    // This ensures table updates immediately when data prop changes
    // Check if data is defined (not undefined/null) - empty array is valid
    if (data !== undefined && data !== null) {
      // Data prop is provided - use it directly (already paginated and sorted by server)
      return data;
    }

    // Fallback: use API response data if no data prop
    if (config.enableServerPagination && apiCall) {
      // Server-side: use API data directly (already sorted and paginated)
      return apiResponse?.data || [];
    }

    // Client-side: process static data (shouldn't reach here if data prop is always provided)
    let result = [...(data || [])];

    // Client-side sorting
    if (!config.enableServerSort && sort.field) {
      const sortField = sort.field;
      result.sort((a, b) => {
        const aValue = (a as Record<string, unknown>)[sortField] as string | number;
        const bValue = (b as Record<string, unknown>)[sortField] as string | number;

        let comparison = 0;
        if (aValue < bValue) comparison = -1;
        else if (aValue > bValue) comparison = 1;

        return sort.order === 'desc' ? -comparison : comparison;
      });
    }

    // Client-side pagination
    if (!config.enableServerPagination) {
      const startIndex = pagination.page * pagination.rowsPerPage;
      const endIndex = startIndex + pagination.rowsPerPage;
      result = result.slice(startIndex, endIndex);
    }

    return result;
  }, [
    apiResponse?.data,
    data,
    sort,
    pagination,
    config.enableServerSort,
    config.enableServerPagination,
    apiCall,
  ]);

  // Final pagination info
  const finalPagination = useMemo((): PaginationConfig => {
    if (apiResponse && config.enableServerPagination) {
      return {
        page: (apiResponse.pagination.currentPage || 1) - 1, // Convert to 0-based
        rowsPerPage: apiResponse.pagination.recordsPerPage || pagination.rowsPerPage,
        totalCount: apiResponse.pagination.totalRecords || 0,
        totalPages: apiResponse.pagination.totalPages || 1,
        rowsPerPageOptions: pagination.rowsPerPageOptions,
      };
    }

    if (config.enableServerPagination) {
      // Loading state - maintain current pagination
      return {
        ...pagination,
        totalCount: 0,
        totalPages: 1,
      };
    }

    // Client-side pagination
    return {
      ...pagination,
      totalCount: data?.length ?? 0,
      totalPages: Math.ceil((data?.length ?? 0) / pagination.rowsPerPage),
    };
  }, [apiResponse, pagination, data?.length, config.enableServerPagination]);

  return {
    tableData,
    loading: apiLoading,
    error: apiError,
    apiResponse,
    pagination: finalPagination,
    handlePageChange,
    handlePageSizeChange,
    sort,
    handleSort,
    refetch,
  };
}
